import { formatMonth, t } from "@gladis/ui/i18n";
/**
 * The decisions behind the estimate's two lines, as functions rather than as JSX.
 *
 * They live here and not in `IndexedEstimate.tsx` because this package's vitest config includes
 * `*.test.ts` and nothing else — deliberately: "Pure unit tests: no HTTP, no database, no external
 * services. If a test here needs any of those, the thing it is testing is in the wrong layer."
 *
 * A component needs a DOM to test. These rules do not, and they are the part that can be wrong in a
 * way nobody notices: whether a household is asked for a fresh valuation this February, and whether
 * an estimate announces itself as one.
 */

/** February — when WOZ assessments land in the post, and the one month worth asking in (FR-033). */
export const WOZ_MONTH_INDEX = 1;

export type ValuationSourceLabelInput = "manual" | "woz" | "index" | "avm";

/**
 * What each source means to a reader, in their words rather than the column's.
 *
 * "Estimated" must not read as a valuation. An indexed value is the household's own last real number
 * moved by its region's market — it can see no renovation, no extension, no new energy label — and a
 * reader scanning the history has to be able to tell which rows are which at a glance.
 *
 * `avm` is in the enum and written by nothing: Altum AI was dropped on commercial grounds before it
 * ever ran. It is handled rather than left to fall through, because an unhandled value would render
 * as a raw enum string in the one column whose whole job is to be legible.
 */
export function valuationSourceLabel(source: ValuationSourceLabelInput): string {
  switch (source) {
    case "manual":
      return t("prop.source.manual");
    case "woz":
      return t("prop.source.woz");
    case "index":
      return t("prop.source.index");
    default:
      return t("prop.source.fetched");
  }
}

export interface EstimateSubject {
  indexable: boolean;
  /** Date of the newest member-entered valuation, or `null` when the current value is itself real. */
  estimatedSince: string | null;
}

/**
 * Whether to invite a fresh valuation this month (FR-033).
 *
 * Three conditions, and each excludes a case where asking would be wrong:
 *
 *   - Not indexable — a French property is never carried forward, so there is nothing drifting.
 *   - Not February — WOZ assessments arrive then; asking in July is asking for a number the
 *     household does not have yet.
 *   - `estimatedSince === null` — the current value is already member-entered. It is as anchored as
 *     it can be, and prompting would be nagging somebody who just did the thing.
 *
 * The YEAR is what decides, not the month-and-day: a property valued by hand in January needs no
 * prompt three weeks later, and one last valued in 2024 does.
 */
export function needsAnnualValuationPrompt(subject: EstimateSubject, today: Date): boolean {
  if (!subject.indexable) return false;
  if (today.getMonth() !== WOZ_MONTH_INDEX) return false;
  if (subject.estimatedSince === null) return false;

  return Number(subject.estimatedSince.slice(0, 4)) < today.getFullYear();
}

/**
 * "March 2024".
 *
 * A MONTH is the honest precision for "when was this last real". A day implies the valuation was an
 * event with a timestamp; it was somebody's estimate of what the house was worth around then, and
 * rendering `2024-03-01` invites a reader to treat the first of the month as meaningful.
 */
export function formatValuationMonth(isoDate: string): string {
  const [year, month] = isoDate.split("-");
  return formatMonth(Number(year), Number(month));
}
